import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthState, IUser, userT } from '../../types/auth';
import { logger } from '../../utils/logger';
import { CognitoService } from '../../services/api/auth/cognito.service';
import { resourceEndpoints } from '../../services/api/endpoints/resource.endpoint';

const initialState: AuthState = {
  user: null,
  role: null,
  token: null,
  clientResourceId: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  clientId: null,
  promoCode: null,
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }) => {
    try {
      const response = await CognitoService.signIn(email, password);

      if ('user' in response && 'role' in response) {
        let clientResourceId: number | null = null;
        // check the cognito emailid matches the one of the resource mail id
        if (response.role === 'client_admin' || response.role === 'client_user') {
          if (!response.clientId) {
            throw new Error('No Client ID found');
          }
          try {
            const resourceData = await resourceEndpoints.listResources(response.clientId);
            const matchedResource = resourceData?.find((res: any) => res.email === email);
            clientResourceId = matchedResource?.id || null;
          } catch (resErr) {
            logger.warn('Unable to fetch resources, proceeding with null clientResourceId', resErr);
            clientResourceId = null;
          }
        }

        logger.info('Login successful', response);

        return {
          user: response.user,
          role: response.role,
          clientId: response.clientId,
          token: response.token,
          clientResourceId,
          promoCode: response.promoCode,
        };
      }

      logger.info('Login successful', response);
      return {
        user: response.user,
        role: response.role,
        clientId: response.clientId,
        token: response.token,
        clientResourceId: null,
        promoCode: response.promoCode,
      };
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logout', async () => {
  await CognitoService.signOut();
  localStorage.clear();
  setTimeout(() => location.reload());
});

export const checkAuth = createAsyncThunk('auth/check', async () => {
  const response = await CognitoService.getCurrentUser();
  if (!response) {
    throw new Error('Not authenticated');
  }

  console.log('[checkAuth] getCurrentUser response:', {
    email: response.user.email,
    role: response.role,
    clientId: response.clientId,
    hasPromoCode: !!response.promoCode,
  });

  let clientResourceId: number | null = null;
  if ((response.role === 'client_admin' || response.role === 'client_user') && response.clientId) {
    try {
      const resourceData = await resourceEndpoints.listResources(response.clientId);
      console.log('[checkAuth] Resources fetched:', {
        count: resourceData?.length,
        emails: resourceData?.map((r: any) => r.email),
        lookingFor: response.user.email,
      });
      const matchedResource = resourceData?.find((res: any) => res.email === response.user.email);
      clientResourceId = matchedResource?.id || null;
      console.log('[checkAuth] Resource match result:', {
        matched: !!matchedResource,
        clientResourceId,
      });
    } catch (resErr) {
      logger.warn('Unable to fetch resources during checkAuth', resErr);
      console.log('[checkAuth] Failed to fetch resources:', resErr);
      clientResourceId = null;
    }
  } else {
    console.log('[checkAuth] Skipping resource lookup:', {
      role: response.role,
      clientId: response.clientId,
      reason: !response.clientId ? 'No clientId' : 'Role not client_admin or client_user',
    });
  }

  console.log('[checkAuth] Final auth state:', {
    email: response.user.email,
    role: response.role,
    clientId: response.clientId,
    clientResourceId,
  });

  return {
    user: response.user,
    role: response.role,
    clientId: response.clientId,
    promoCode: response.promoCode,
    clientResourceId,
  };
});

// New async thunks to add
export const signUpUser = createAsyncThunk('auth/signup', async (user: userT) => {
  try {
    return await CognitoService.signUp(user);
  } catch (error) {
    logger.error('Signup error:', error);
    throw error;
  }
});

export const confirmSignup = createAsyncThunk(
  'auth/confirmSignup',
  async ({ username, code }: { username: string; code: string }) => {
    try {
      return await CognitoService.confirmCode(username, code);
    } catch (error) {
      logger.error('Confirm signup error:', error);
      throw error;
    }
  }
);

export const autoSignIn = createAsyncThunk(
  'auth/autoSignIn',
  async (creadentials: { email: string; password: string }) => {
    try {
      return await CognitoService.userSignTemp(creadentials.email, creadentials.password);
    } catch (error) {
      logger.error('Auto sign in error:', error);
      throw error;
    }
  }
);

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async ({ password, newPassword }: { password: string; newPassword: string }) => {
    try {
      const data = {
        password,
        newPassword,
      };
      return await CognitoService.updatePassword(data);
    } catch (error) {
      logger.error('Update password error:', error);
      throw error;
    }
  }
);

export const resendConfirmationCode = createAsyncThunk('auth/resendCode', async (email: string) => {
  try {
    return await CognitoService.resendSignUpCode(email);
  } catch (error) {
    logger.error('Resend code error:', error);
    throw error;
  }
});

export const forgetPassword = createAsyncThunk('auth/forgetPassword', async (email: string) => {
  try {
    const result = await CognitoService.forgetPassword(email);
    return result;
  } catch (error) {
    logger.error('Forget password error:', error);
    throw error;
  }
});

export const confirmNewPassword = createAsyncThunk(
  'auth/confirmNewPassword',
  async (user: IUser) => {
    try {
      const result = await CognitoService.confirmNewPassword(user);
      return result;
    } catch (error) {
      logger.error('Confirm new password error:', error);
      throw error;
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: state => {
      state.error = null; // Reset error state
    },
    setClientResourceId: (state, action) => {
      state.clientResourceId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user || null;
        state.role = action.payload.role || null;
        state.token = action.payload.token || null;
        state.clientId = action.payload.clientId || null;
        state.clientResourceId = action.payload.clientResourceId || null;
        state.promoCode = action.payload.promoCode || null;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Authentication failed';
      })
      .addCase(logoutUser.fulfilled, () => {
        return initialState;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.clientId = action.payload.clientId;
        state.promoCode = action.payload.promoCode || null;
        state.clientResourceId = action.payload.clientResourceId || null;
        state.error = null;
      })
      .addCase(checkAuth.rejected, () => {
        return initialState;
      })
      .addCase(signUpUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.isLoading = false;
        // You can store the user or handle the response here if needed
        state.error = null;
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Signup failed';
      })
      .addCase(confirmSignup.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmSignup.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(confirmSignup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Confirmation failed';
      })
      // resendConfirmationCode cases
      .addCase(resendConfirmationCode.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendConfirmationCode.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resendConfirmationCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Resend code failed';
      })
      // autoSignIn cases
      .addCase(autoSignIn.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(autoSignIn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user || null;
        state.role = action.payload.role || null;
        state.token = action.payload.token || null;
        state.error = null;
      })
      .addCase(autoSignIn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Auto sign-in failed';
      })
      .addCase(forgetPassword.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgetPassword.fulfilled, state => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(forgetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Forget password failed';
      })
      .addCase(confirmNewPassword.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmNewPassword.fulfilled, state => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(confirmNewPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Confirm new password failed';
      });
  },
});

export const { setError, clearError, setClientResourceId } = authSlice.actions;
export default authSlice.reducer;
